import { apiClient } from '@/services/api';

/**
 * Append the selected GloboGraphics site (sigla) as a `site` query param. Header-less
 * <img> requests can't send the x-globographics-site header, so without this the API
 * resolves its *default* site and 404s when that's the wrong PDS/Graphic Hub.
 * withSiteScope reads `?site=` too, so carrying the selected sigla here routes the
 * image to the right site. No selection → unchanged (API falls back to the default).
 * Key mirrors sites.js (@socialcam/selected_site).
 */
export function appendSiteParam(url) {
  if (!url) return url;
  const sigla = localStorage.getItem('@socialcam/selected_site');
  if (!sigla) return url;
  return url + (url.includes('?') ? '&' : '?') + 'site=' + encodeURIComponent(sigla);
}

/**
 * Build the static-preview thumbnail URL for an inline (MOS) playlist item.
 * Inline items have no cena, so their preview is served per item id. `bust` overrides
 * the cache-buster after a lazy on-view render (the item's dsc_preview_hash isn't
 * known yet then).
 * Returns: {baseURL}/playout/playlists/items/{id}/preview/image?v={hash|bust}
 */
export function playoutItemPreviewUrl(item, bust) {
  const base = (apiClient.defaults.baseURL || '').replace(/\/+$/, '');
  const v = bust != null ? bust : (item.dsc_preview_hash || '');
  return appendSiteParam(`${base}/playout/playlists/items/${item.id_playlist_item}/preview/image?v=${v}`);
}

/**
 * Build the MSE-thumbnail URL for a MOS item — the MSE's own high-fidelity element
 * snapshot (proxied). Preferred over our own render: the MSE supplies the correct
 * per-element context (concept/variant + FULL scene), so complex templates render
 * filled. Cache-buster hashes the stored MSE URL so it reloads only if that changes.
 * `width` (px) requests a larger render for the big preview — the MSE bakes a
 * small default (~256px), soft when shown large.
 * Returns: {baseURL}/playout/playlists/items/{id}/mos-thumbnail?v={hash}[&w={width}]
 */
export function playoutItemMosThumbnailUrl(item, bust, width) {
  const base = (apiClient.defaults.baseURL || '').replace(/\/+$/, '');
  const url = item.dsc_mos_thumbnail_url || '';
  let v = bust;
  if (v == null) {
    let h = 0;
    for (let i = 0; i < url.length; i++) h = (Math.imul(31, h) + url.charCodeAt(i)) | 0;
    v = Math.abs(h);
  }
  const w = width ? `&w=${width}` : '';
  return appendSiteParam(`${base}/playout/playlists/items/${item.id_playlist_item}/mos-thumbnail?v=${v}${w}`);
}
